import React, { useMemo, Fragment, useEffect, useState } from "react";
import {
  MaterialReactTable,
  MRT_FullScreenToggleButton as MRTFullScreenToggleButton,
  MRT_ShowHideColumnsButton as MRTShowHideColumnsButton,
  MRT_ToggleFiltersButton as MRTToggleFiltersButton,
} from "material-react-table";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { Link, useNavigate } from "react-router-dom";
import {
  useGetAdminMessagesListQuery,
  useDeleteAdminMessageByIdMutation,
} from "../../../reactQueries/AppApi/rqHooksForAdminMessages";

import Button from "@mui/material/Button";
import { dateformatConverter } from "../../../common/utilities/dates/dateformatConverter";
import { adminMsgCreatePath, adminMsgEditPath } from "../../../routes/routePaths";
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import MpgConfirmDialog from "../../../common/dialog/MpgConfirmDialog"
import NoTableDataComp from "../../../common/otherCommonComponents/NoTableDataComp";
import MpgErrorDialog from "../../../common/dialog/MpgErrorDialog";
import moment from 'moment';
import HtmlTooltip from "../../../common/tooltips/HtmlTooltip";
import useTrackUserActivity from "../../../userActivityTracking/useTrackUserActivity";
import { getAccessTypes } from "../../../hooksAndFunctions/functions/getAccessTypes";
import ExportButton from "../../../common/otherCommonComponents/ExportButton";
import {
  useGetSiteCoreCMSData,
  useTrackActivityMutation,
} from "../../../reactQueries/commonRQHooks";
import { useGetLoggedInUserDetails } from "../../../hooksAndFunctions/customHooks/useGetLoggedInUserDetails";

const TooltipCell = ({ renderedCellValue }) => (
  <HtmlTooltip title={renderedCellValue}>{renderedCellValue}</HtmlTooltip>
);

const returnLabel = (key, labelArray = []) => {
  return labelArray.find((label) => label.Key === key)?.Phrase || '';
};

const ViewAdminMessages = () => {

  const { mutate: trackActivityMutate } = useTrackActivityMutation();
    const { loggedInUser } = useGetLoggedInUserDetails();
      const {id: userId } = loggedInUser;

      const { isEnterpriseAdmin: isEnterpriseAdminType, clientPortalAdminType } = getAccessTypes()
      const {
          hasA1Access,
          hasA4Access
      } = clientPortalAdminType
  
      const canExportXls = isEnterpriseAdminType || hasA1Access;
  const {
    isSuccess,
    isFetching: isFetchingOnAdminMsgData,
    data: messagesData,
    isError,
    refetch: refetchAdminMessagesList,
  } = useGetAdminMessagesListQuery();

  const { data: adminMessageCMSDataFromAPI } = useGetSiteCoreCMSData({
    pagename: "AdminMessages",
  });

  const [adminMessagesCMSData, setAdminMessagesCMSData] = useState({title: "", description: "", tabNavigationTitle: "",pageContent: {}});

  const fetchAdminMessageCMSData = () =>{
    adminMessageCMSDataFromAPI?.Pages?.forEach((el) => {
       if (el.Key === "AdminMessages") {
         const { Title, Description, TabNavigationTitle, Components } = el;
         setAdminMessagesCMSData({ title: Title, description: Description, tabNavigationTitle: TabNavigationTitle, pageContent: Components });
       }
    });
 }

 const fetchAdminMessageListLabel = (key) => {
  const labels = adminMessagesCMSData?.pageContent?.AdminMessagesListComponent?.Labels;
  return returnLabel(key, labels);
}

 const fetchCommonCMSLabel = (key) => {
  const labels = adminMessagesCMSData?.pageContent?.CommonComponent?.Labels;
  return returnLabel(key, labels);
}

  const adminMessagesTitle = adminMessagesCMSData?.title;
  const exportFileName = `${adminMessagesTitle}.xlsx`;
  const exportWorksheetName = adminMessagesTitle;

 const exportColumns = [
    { id: 'MessageHeader', header: fetchAdminMessageListLabel('AdminMessagesList.MessageHeader'), getValue: (row) => row?.MessageHeader || '' },
    { id: 'MessageContent', header: fetchAdminMessageListLabel('AdminMessagesList.MessageContent'), getValue: (row) => row?.MessageContent || '' },
    { id: 'Language', header: fetchAdminMessageListLabel('AdminMessagesList.Language'), getValue: (row) => row?.Language || '' },
    { id: 'isPersistent', header: fetchAdminMessageListLabel('AdminMessagesList.Persistent'), getValue: (row) => row?.Persistent ? 'Yes' : 'No' },
    { id: 'Status', header: fetchAdminMessageListLabel('AdminMessagesList.Status'), getValue: (row) => row?.Status || '' },
    { id: 'TimeZone', header: fetchAdminMessageListLabel('AdminMessagesList.Timezone'), getValue: (row) => row?.TimeZone || '' },
    { id: 'fromDate', header: fetchAdminMessageListLabel('AdminMessagesList.FromDate'), getValue: (row) => row?.FromDate ? dateformatConverter(moment.tz(row.FromDate, `${row?.TimeZoneDetails?.IANATimeZoneId}`), "DD MMM YYYY hh:mm A") : '' },
    { id: 'toDate', header: fetchAdminMessageListLabel('AdminMessagesList.ToDate'), getValue: (row) => row?.ToDate ? dateformatConverter(moment.tz(row.ToDate, `${row?.TimeZoneDetails?.IANATimeZoneId}`), "DD MMM YYYY hh:mm A") : '' },
    { id: 'CreatedBy', header: fetchAdminMessageListLabel('AdminMessagesList.CreatedBy'), getValue: (row) => row?.CreatedBy || '' },
    { id: 'createdTimeStamp', header: fetchAdminMessageListLabel('AdminMessagesList.CreatedWhen'), getValue: (row) => row?.CreatedTimeStamp ? dateformatConverter(row.CreatedTimeStamp, "DD MMM YYYY hh:mm A") : '' },
  ];

useEffect(() => {
  fetchAdminMessageCMSData();
}, [adminMessageCMSDataFromAPI]);

  const columns = useMemo(
    () => [
      {
        accessorKey: "MessageHeader",
        header: fetchAdminMessageListLabel('AdminMessagesList.MessageHeader'),
        Cell: TooltipCell,
        filterFn: 'contains',
        muiTableHeadCellFilterTextFieldProps: { placeholder:fetchAdminMessageListLabel("AdminMessagesList.PlaceholderMessageHeader") }
 
      },
      {
        accessorKey: "MessageContent",
        header: fetchAdminMessageListLabel('AdminMessagesList.MessageContent'),
        Cell: TooltipCell,
        filterFn: 'contains',
        muiTableHeadCellFilterTextFieldProps: { placeholder:fetchAdminMessageListLabel("AdminMessagesList.PlaceholderMessageContent") }
      },
      {
        accessorKey: "Language",
        header:fetchAdminMessageListLabel('AdminMessagesList.Language'),
        muiTableHeadCellFilterTextFieldProps: { placeholder:fetchAdminMessageListLabel("AdminMessagesList.PlaceholderLanguage") }
      },
      {
        id: 'isPersistent',
        header: fetchAdminMessageListLabel('AdminMessagesList.Persistent'),
        accessorFn: (originalRow) => {
          const val = originalRow.Persistent
          return val ? "Yes" : "No"
        }, //must be strings
        filterFn: 'contains',
        muiTableHeadCellFilterTextFieldProps: { placeholder:fetchAdminMessageListLabel("AdminMessagesList.PlaceholderPersistent") }
      },
      {
        accessorKey: "Status",
        header: fetchAdminMessageListLabel('AdminMessagesList.Status'),
        filterFn: 'contains',
        muiTableHeadCellFilterTextFieldProps: { placeholder:fetchAdminMessageListLabel("AdminMessagesList.PlaceholderStatus") }
      },
      {
        accessorKey: "TimeZone",
        header: fetchAdminMessageListLabel('AdminMessagesList.Timezone'),
        Cell: TooltipCell,
        muiTableHeadCellFilterTextFieldProps: { placeholder:fetchAdminMessageListLabel("AdminMessagesList.PlaceholderTimezone") }
      },
      {
        accessorFn: (originalRow) => {
          const d = moment.tz(originalRow.FromDate, `${originalRow?.TimeZoneDetails?.IANATimeZoneId}`)
          console.log("originalRow", originalRow?.TimeZoneDetails?.IANATimeZoneId)
          const data = dateformatConverter(d, "DD MMM YYYY hh:mm A");

          return `${data}`
        },
        id: 'fromDate',
        header:  fetchAdminMessageListLabel('AdminMessagesList.FromDate'),
        filterFn: 'contains',
        sortingFn: 'datetime',
        muiTableHeadCellFilterTextFieldProps: { placeholder:fetchAdminMessageListLabel("AdminMessagesList.PlaceholderFromDate") }
      },
      {
        accessorFn: (originalRow) => {
          const d = moment.tz(originalRow.ToDate, `${originalRow?.TimeZoneDetails?.IANATimeZoneId}`)
          const data = dateformatConverter(d, "DD MMM YYYY hh:mm A");

          return `${data}`
        },
        id: 'toDate',
        header: fetchAdminMessageListLabel('AdminMessagesList.ToDate'),
        filterFn: 'contains',
        sortingFn: 'datetime',
        muiTableHeadCellFilterTextFieldProps: { placeholder:fetchAdminMessageListLabel("AdminMessagesList.PlaceholderToDate") }
      },
      {
        accessorKey: "CreatedBy",
        header: fetchAdminMessageListLabel('AdminMessagesList.CreatedBy'),
        muiTableHeadCellFilterTextFieldProps: { placeholder:fetchAdminMessageListLabel("AdminMessagesList.PlaceholderCreatedBy") }
      },
      {
        accessorFn: (originalRow) => {
          const data = dateformatConverter(originalRow.CreatedTimeStamp, "DD MMM YYYY hh:mm A");
          return `${data}`
        },
        id: 'createdTimeStamp',
        header: fetchAdminMessageListLabel('AdminMessagesList.CreatedWhen'),
        filterFn: 'contains',
        sortingFn: 'datetime',
        muiTableHeadCellFilterTextFieldProps: { placeholder:fetchAdminMessageListLabel("AdminMessagesList.PlaceholderCreatedWhen") }
      },

    ],
    [adminMessagesCMSData]
  );

  /**redirect hook */
  let navigate = useNavigate();
  const {
    mutate: deleteAdminMessageById,
    isSuccess: isSuccessOnDelete,
    variables: variablesOnDelete
  } = useDeleteAdminMessageByIdMutation();

  useEffect(() => {
    if (isSuccessOnDelete) {
      void refetchAdminMessagesList();
    }
  }, [isSuccessOnDelete]);

  /**confirm dialog state on delete action */
  const [openMpgConfirmDialog, setOpenMpgConfirmDialog] = useState(false)
  const [idTobeDeletedOrRecovered, setIdTobeDeletedOrRecovered] = useState("")


  /**tracking user Activity */
  const { trackActivity } = useTrackUserActivity()
  useEffect(() => {
    if (isSuccessOnDelete) {
      trackActivity({
        activitySuccess: isSuccessOnDelete,
        actionType: variablesOnDelete?.isDeleteMode ? "Deleted" : "Recovered",
        itemId: variablesOnDelete?.Id || "",
      })
    }
  }, [variablesOnDelete, isSuccessOnDelete])

  return (
    <Fragment>
      {
        !!isFetchingOnAdminMsgData && fetchCommonCMSLabel('Common.Waitloading')
      }

      <Typography variant="h5" align="center" sx={{ fontWeight: "bolder" }}>
        {adminMessagesCMSData.title}
      </Typography>

      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems="center"
        spacing={1}
        m={1}
      >
        {
          !hasA4Access && (
            <Button
              variant="outlined"
              size="small"
              component={Link}
              to={adminMsgCreatePath}
            >
              {fetchAdminMessageListLabel('AdminMessagesList.PublishNewAdminMessage')}
            </Button>
          )
        }

      </Stack>

      {/**incase successful call but empty response */}
      {!!isSuccess && !messagesData?.length && (<NoTableDataComp />)}

      {!!isSuccess && !!messagesData?.length && (
        <MaterialReactTable
          columns={columns}
          data={messagesData || []}
          layoutMode="grid"
          enableGlobalFilter={false}
          enableDensityToggle={false}
          initialState={{
            density: "compact",
            showColumnFilters: true,
          }}
          state={{
            isLoading: isFetchingOnAdminMsgData,
            columnPinning: {
              left: ["mrt-row-actions", "MessageHeader"],
            },
          }}
          displayColumnDefOptions={{
            'mrt-row-actions': {
              header: fetchAdminMessageListLabel('AdminMessagesList.Actions'), //change header text
              size: 100, //make actions column wider
            },
          }}
          muiTablePaginationProps={{
            labelRowsPerPage: fetchCommonCMSLabel('Common.Rowsperpage') //Rows per page
          }}
           renderToolbarInternalActions={({ table }) => (
                      <>
                        {canExportXls && (
                          <ExportButton
                            table={table}
                            tooltipTitle={fetchAdminMessageListLabel("AdminMessagesList.ExportToXLS")}
                            trackActivity={trackActivityMutate}
                            userId={userId}
                            exportColumns={exportColumns}
                            fileName={exportFileName}
                            worksheetName={exportWorksheetName}
                          />
                        )}
                        <MRTToggleFiltersButton table={table} />
                        <MRTShowHideColumnsButton table={table} />
                        <MRTFullScreenToggleButton table={table} />
                      </>
                    )}
          enableStickyHeader
          enableRowActions
          renderRowActions={({ row, table }) => {
            const Id = row.original.Id;
            const Status = row?.original.Status;
            return (
              <Box sx={{ display: 'flex', flexWrap: 'nowrap', gap: '8px' }}>
                <IconButton
                  onClick={() => {
                    navigate(adminMsgEditPath({ id: Id }));
                  }}
                >
                  <EditIcon />
                </IconButton>


                <IconButton
                  onClick={() => {
                    setOpenMpgConfirmDialog(true)
                    setIdTobeDeletedOrRecovered(Id)

                  }}
                  /** delete action is not accessible for A4 role */
                  disabled={hasA4Access || Status === "Deleted"}
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
            )
          }}
        />
      )}

      {/** confirm dialog on delete action */}
      <MpgConfirmDialog
        title={fetchAdminMessageListLabel('AdminMessagesList.PopUpDeleteConfirmationTitle')}
        content={fetchAdminMessageListLabel('AdminMessagesList.PopUpDeleteConfirmationMsg')}
        open={openMpgConfirmDialog}
        setOpen={setOpenMpgConfirmDialog}
        extraLogicHandlerOnAgree={() => {
          deleteAdminMessageById({ Id: idTobeDeletedOrRecovered });
          setIdTobeDeletedOrRecovered("")
        }}
      />

      <MpgErrorDialog
        open={!!isError}
      />
    </Fragment>
  );
};

export default ViewAdminMessages;
